import config from '../config.json';

let refreshPromise = null;

const buildApiBaseUrl = ({ host, domain, subDomain, apiSubDomain }) => {
  let apiBaseUrl;
  if (host.indexOf('localhost') > -1) {
    apiBaseUrl = `http://localhost:${config.port}/api`;
  } else {
    // detect env
    const parts = host.split('.');
    // Only qa/uat are real environments; any other segment before 'prosolve.com' means prod
    const envPart = parts[parts.length - 3];
    const env = ['qa', 'uat'].includes(envPart) ? `.${envPart}` : '';
    apiBaseUrl = `https://${apiSubDomain}.${subDomain}${env}.${domain}/api`;
  }
  return apiBaseUrl;
}

const domain = 'prosolve.com';
const subDomain = 'go';
const apiSubDomain = 'api';

// Base url of this portal's own API. Exported for the few places that need a plain url instead of
// a fetch (e.g. anchor-tag downloads in AssetManager/downloadUtils.js).
export const getApiBaseUrl = () => buildApiBaseUrl({ host: window.location.host, domain, subDomain, apiSubDomain })

export default () => {
  const host = window.location.host;

  const apiBaseUrl = buildApiBaseUrl({ host, domain, subDomain, apiSubDomain })

  const rootBaseUrl = buildApiBaseUrl({ host, domain, subDomain: 'root', apiSubDomain })

  // Single-flight refresh helper
  const refreshAccessToken = async () => {
    if (!refreshPromise) {
      refreshPromise = fetch(`${rootBaseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        // header optional, but often nice to include
        headers: { 'Content-Type': 'application/json' },
      }).finally(() => {
        refreshPromise = null;
      });
    }

    const res = await refreshPromise;
    if (!res.ok) {
      // refresh failed (401/403/etc.) => treat as logged out upstream
      const err = new Error(`Refresh failed with status ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return res;
  };

  return async (url, options = {}, ...args) => {
    // Ensure credentials always include cookies
    const optionsOverride = {
      ...options,
      credentials: 'include',
    };

    const normalizedUrl = String(url).replace(/^\//, '');
    const isAuthVerifyOrMe = normalizedUrl.startsWith('auth/verify') || normalizedUrl.startsWith('auth/me')

    // Essentially we need to always route to the root apis 
    // if we are auth/verify, auth/me (or auth/refresh but thats forced there by other logic below)
    const apiFetchBaseUrl = isAuthVerifyOrMe ? rootBaseUrl : apiBaseUrl;

    // Helper to perform the actual request (so we can retry cleanly)
    const doRequest = () => fetch(`${apiFetchBaseUrl}/${url}`, optionsOverride, ...args);

    let res = await doRequest();

    // If unauthorized, try refresh once then retry request once
    if (res.status === 401) {
      // Prevent infinite loop: don't try to refresh if we're already calling refresh
      // (handles cases where caller passes url like "auth/refresh" or "/auth/refresh")
      if (!normalizedUrl.startsWith('auth/refresh')) {
        try {
          await refreshAccessToken();
          res = await doRequest(); // retry once
        } catch (e) {
          // Only force logout on "auth is invalid" responses.
          // If refresh failed due to 401/403 -> token expired/invalid -> logout.
          if (e?.status === 401 || e?.status === 403) {
            // Trigger logout event
            window.dispatchEvent(new CustomEvent("auth:logout", {}));
          }
          // If refresh fails, return the original 401 response (or you can throw)
          // Minimal-change behavior: just return the 401 response so callers can handle it.
          return res;
        }
      }
    }

    return res;
  };
};
